import { useState, type ReactNode } from 'react';
import { ShieldAlert, Loader2 } from 'lucide-react';
import styles from './DisclaimerModal.module.css';

export interface DisclaimerModalProps {
  /** Modal title — varies by side ("About sharing your charger" /
   *  "About using a peer's charger"). */
  title: string;
  /** Body copy. Plain string or pre-formatted JSX. */
  body: ReactNode;
  /** Async ack handler. The button stays disabled while it
   *  resolves; rejection surfaces as an inline error. */
  onAck: () => Promise<void>;
  /** Optional cancel handler. When omitted, the modal has no
   *  cancel affordance — the resident MUST ack to proceed. */
  onCancel?: () => void;
}

/**
 * Shared first-encounter disclaimer modal for the resident-managed
 * peer-to-peer flow. Both sides see one of these once: the owner on
 * their first pass-list add, the recipient on their first /my-access
 * load with a non-empty list.
 *
 * Design intent: unmissable but not threatening. Single primary
 * action ("I understand"). The tap IS the ack — no checkbox; the
 * server-side endpoint stamps the timestamp idempotently so a
 * double-tap or replay doesn't roll the audit forward.
 *
 * Backdrop click and ESC are NOT escape hatches: the user must take
 * the explicit action to dismiss. This is a legal/product constraint
 * — implicit dismissal would muddy the audit trail.
 */
export function DisclaimerModal({
  title, body, onAck, onCancel,
}: DisclaimerModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleAck = async (): Promise<void> => {
    setSubmitting(true);
    setErrorMsg('');
    try {
      await onAck();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to record acknowledgment');
      setSubmitting(false);
    }
    // Note: on success we leave `submitting` true. The parent
    // tears down the modal, so the brief disabled-state flicker is
    // hidden by unmount.
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal aria-labelledby="evi-disclaimer-title">
      <div className={styles.card}>
        <div className={styles.iconWrap} aria-hidden>
          <ShieldAlert size={28} />
        </div>
        <h2 id="evi-disclaimer-title" className={styles.title}>{title}</h2>
        <div className={styles.body}>{body}</div>

        {errorMsg && (
          <div className={styles.error} role="alert">{errorMsg}</div>
        )}

        <div className={styles.actions}>
          {onCancel && (
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onCancel}
              disabled={submitting}
            >
              Not now
            </button>
          )}
          <button
            type="button"
            className={styles.ackBtn}
            onClick={handleAck}
            disabled={submitting}
          >
            {submitting
              ? <><Loader2 size={14} className={styles.spin} /> Saving…</>
              : 'I understand'}
          </button>
        </div>
      </div>
    </div>
  );
}
